import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Formik } from 'formik';
import React, { FunctionComponent, ReactNode, useCallback } from 'react';
import { Loader, Modal } from 'semantic-ui-react';
import { ToastError, ToastSuccess } from 'utils';
import { AuthUserFormFields } from './auth-user-form-field.type';
import authUserFormValidationSchema from './auth-user-form-validation-schema';
import { getKeycloakUser, updateKeycloakUser } from 'utils';

interface AccountFormUpdateProps {
  userId: string;
  children?: ReactNode;
  onUserUpdated?: () => void;
}

const AccountFormUpdate: FunctionComponent<AccountFormUpdateProps> = ({
  userId: accountId,
  children,
  onUserUpdated,
}) => {
  const queryClient = useQueryClient();

  const { data: account, isLoading } = useQuery({
    queryKey: ['keycloakUser', accountId],
    queryFn: () => getKeycloakUser(accountId),
  });

  const { mutate: updateUser } = useMutation({
    mutationFn: (variables: { userId: string; email: string }) =>
      updateKeycloakUser(variables.userId, { email: variables.email }),
    onError: () => {
      ToastError(
        'Erreur',
        "Une erreur est survenue lors de la modification de l'adresse email",
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keycloakUser', accountId] });
      onUserUpdated?.();
      ToastSuccess('Succès', 'Adresse email modifiée');
    },
  });

  const onSubmit = useCallback(
    async (values: AuthUserFormFields) => {
      updateUser({ userId: accountId, email: values.email });
    },
    [updateUser, accountId],
  );

  if (isLoading) {
    return <Loader active inline="centered" />;
  }

  if (!account) {
    return (
      <Modal.Content>
        <p>
          Une erreur est survenue lors de la récupération des informations du
          compte. Veuillez réessayer.
        </p>
      </Modal.Content>
    );
  }

  const initialValues: AuthUserFormFields = {
    email: account.email,
  };

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={authUserFormValidationSchema(account.email)}
    >
      {children}
    </Formik>
  );
};

export default AccountFormUpdate;
